import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
//TODO: Captcha response handling
export interface LoginResponse {
  success: boolean;
  requiresVerification: boolean;
  message?: string;
  title: string;
  status: number;
  detail: string;
}

export interface VerificationResponse {
  success: boolean;
  message?: string;
}

export interface VerifyOTPResponse {
  success: boolean;
}


export interface RegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  attorneyConfirmed: boolean;
  termsAccepted: boolean;
  recaptchaToken: string;
}

export interface RegistrationNotification {
  sent: boolean;
  notificationId: string;
  status: string;
  channel: string;
  sentTo: string;
  errorMessage: string | null;
  sentAt: string;
}

export interface RegistrationResponse {
  success: boolean;
  message: string;
  userId: number;
  notification: RegistrationNotification;
  roleAssigned: boolean;
  assignedRole: string;
  roleAssignmentError: string | null;
}

export interface InnerException {
  message: string;
  type: string;
  stackTrace?: string;
}

export interface RegistrationErrorResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  correlationId: string;
  errorCode: string;
  timestamp: string;
  // Validation errors by field name
  errors?: Record<string, string | string[]>;
  stackTrace?: string;
  innerException?: InnerException;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private dashApiUrl = environment.dashApiUrl;

  isAuthenticated = signal(false);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.checkAuthStatus().subscribe();
    }
  }

  //No password used in login - must use verification code
  sendVerificationCode(emailOrPhone: string, captchaToken: string): Observable<any> {
    return this.postOtpRequest(emailOrPhone, captchaToken);
  }

  resendVerificationCode(emailOrPhone: string, captchaToken: string): Observable<any> {
    return this.postOtpRequest(emailOrPhone, captchaToken);
  }

  private postOtpRequest(emailOrPhone: string, captchaToken: string): Observable<any> {
    const email = emailOrPhone.includes('@') ? emailOrPhone : '';
    const phoneNumber = emailOrPhone.includes('@') ? '' : emailOrPhone;

    return this.http.post(
      `${this.dashApiUrl}/api/OTP/sendOTP`,
      { email, phoneNumber, recaptchaToken: captchaToken },
      { responseType: 'text' }
    );
  }

  register(userData: RegistrationRequest): Observable<RegistrationResponse> {
    //The signup endpoint also generates a OTP 
    return this.http.post<RegistrationResponse>(`${this.dashApiUrl}/api/User/signup`, userData);
  }

  //TODO: attestation agreement and privacy policy requests

  verifyCode(otp: string, emailOrPhone: string): Observable<VerifyOTPResponse> {
    const email = emailOrPhone.includes('@') ? emailOrPhone : '';
    const phoneNumber = emailOrPhone.includes('@') ? '' : emailOrPhone;

    return this.http.post<VerifyOTPResponse>(
      `${this.dashApiUrl}/api/OTP/verifyOTP`,
      { email, phoneNumber, otp },
      { withCredentials: true }
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(
      `${this.dashApiUrl}/api/OTP/logout`,
      {},
      { withCredentials: true }
    ).pipe(
      tap(() => this.clearAuth()),
      catchError(() => {
        this.clearAuth();
        return of(void 0);
      })
    );
  }

  checkAuthStatus(): Observable<boolean> {
    return this.http.get<{ authenticated: boolean }>(
      `${this.dashApiUrl}/api/OTP/session`,
      { withCredentials: true }
    ).pipe(
      map(response => response.authenticated),
      tap(authenticated => this.isAuthenticated.set(authenticated)),
      catchError(() => {
        this.isAuthenticated.set(false);
        return of(false);
      })
    );
  }

  markAuthenticated(): void {
    this.isAuthenticated.set(true);
  }

  clearAuth(): void {
    this.isAuthenticated.set(false);
  }
}
